// 출퇴근 시간 설정 회사별 카드 모달 (Phase 1-2 C)
// 회사별 등록 현황 표시 + [수정] 클릭 → onEditCompany(code) 콜백 (외부에서 기존 단일 회사 모달 열기)
import React, { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { btn, primaryBtn } from './styles';

// 회사 목록 = 뷰어 "주회사"(/api/me, 운영자=PINE / 테넌트=자기 회사) + 추가 외부회사(my 엔드포인트).
//   PINE/파인그룹 하드코딩 제거 — 테넌트 관리자에게 PINE 카드가 뜨던 누출 차단. 주회사는 메인 라우트(Fix B 스코프).
// 2026-04-28: companyCodes (배열) 인자 — 해당 코드만 필터 (자기 회사 화면에서 자기 회사만 노출).
//   미지정 → 전체 회사 (이전 동작 유지)
async function fetchCompaniesAndSettings(companyCodes, timeSettingsPathBuilder) {
  // 뷰어 자기 회사(주회사) — /api/me. 운영자=PINE, 테넌트=자기 회사. 하드코딩 없음.
  let primary = null;
  try {
    const me = await apiClient.get('/api/me');
    const co = me.ok ? me.data?.user?.company : null;
    if (co?.code) primary = { code: co.code, name: co.name || co.code };
  } catch (_) { /* ignore */ }
  const primaryCode = primary ? String(primary.code).toUpperCase() : null;

  // 추가 외부회사 (인증만 필요한 my 엔드포인트 — 테넌트 스코프됨)
  let extList = [];
  try {
    const r = await apiClient.get('/api/my/external-companies');
    if (r.ok && Array.isArray(r.data?.list)) extList = r.data.list;
  } catch (_) { /* ignore */ }

  // 주회사 + 추가회사(주회사 중복 제거)
  let companies = [];
  if (primary) companies.push(primary);
  for (const c of extList) {
    if (!primaryCode || String(c.code).toUpperCase() !== primaryCode) {
      companies.push({ code: c.code, name: c.name || c.code });
    }
  }
  // companyCodes 필터 적용 (있으면) — 대문자 정규화
  if (Array.isArray(companyCodes) && companyCodes.length > 0) {
    const allow = new Set(companyCodes.map((s) => String(s || '').toUpperCase()));
    companies = companies.filter((c) => allow.has(String(c.code).toUpperCase()));
  }

  // 각 회사별 settings (주회사=메인 라우트(Fix B 스코프), 그 외=ext 라우트 — 권한 다양 → 실패는 미등록 표시)
  const out = [];
  for (const c of companies) {
    const url = typeof timeSettingsPathBuilder === 'function'
      ? timeSettingsPathBuilder(c.code)
      : String(c.code).toUpperCase() === primaryCode
        ? '/api/admin/attendance-time-settings'
        : `/api/admin/ext/${c.code}/attendance-time-settings`;
    try {
      const r = await apiClient.get(url);
      const tpls = Array.isArray(r.data?.data?.templates) ? r.data.data.templates : [];
      // registered = 그 회사가 시간을 저장한 적 있음(서버 data.saved). 없으면 서버가 기본 시간을 보내 주므로 시간 유무로 가르면 안 된다(2026-10-26).
      out.push({ code: c.code, name: c.name, registered: r.data?.data?.saved === true, templates: tpls, accessible: r.ok });
    } catch (_) {
      out.push({ code: c.code, name: c.name, registered: false, templates: [], accessible: false });
    }
  }
  return out;
}

// 첫 템플릿의 평일(월요일) 시간 표시용 요약
function summarizeFirstTemplate(templates) {
  const t = templates[0];
  if (!t) return null;
  const monRule = t.weekly_rules?.mon;
  if (monRule?.is_workday && monRule.start_time && monRule.end_time) {
    return `${t.name || '기본'} · 출근 ${monRule.start_time} / 퇴근 ${monRule.end_time}`;
  }
  return t.name || '기본';
}

export function AttendanceTimeSettingsModal({ open, onClose, onEditCompany, companyCodes, timeSettingsPathBuilder }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchCompaniesAndSettings(companyCodes, timeSettingsPathBuilder).then((rows) => { setList(rows); setLoading(false); });
    // companyCodes 배열 변경 시 재조회 (deps 직렬화)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(companyCodes || []), timeSettingsPathBuilder]);

  if (!open) return null;
  return (
    <Modal
      open={open}
      icon="⏰"
      title="출퇴근 시간 설정 — 회사별 현황"
      onClose={onClose}
      width="min(560px, 96vw)"
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btn} onClick={onClose}>닫기</button>
        </div>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>불러오는 중...</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {list.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>회사 목록을 불러올 수 없습니다.</div>
          )}
          {list.map((c) => {
            const summary = summarizeFirstTemplate(c.templates);
            return (
              <div key={c.code} style={cardStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* 회사는 작업 대상과 같은 이름으로(코드 X) — 2026-10-26 */}
                  <strong style={{ fontSize: 14 }}>{c.name || c.code}</strong>
                  <span style={{ marginLeft: 'auto', ...(c.registered ? badgeOnStyle : badgeOffStyle) }}>
                    {!c.accessible ? '권한 없음' : c.registered ? '저장됨' : '기본 시간'}
                  </span>
                </div>
                {/* 저장 안 한 회사도 기본 시간으로 계산되므로 그 시간을 그대로 보여 준다(결정 가) */}
                {c.accessible && summary && (
                  <div style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 4 }}>{summary}</div>
                )}
                {c.accessible && (
                  <div style={{ marginTop: 6 }}>
                    <button style={c.registered ? btn : primaryBtn} onClick={() => onEditCompany(c.code)}>
                      {c.registered ? '수정' : '정하기'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

const cardStyle = { border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg-card)' };
const badgeOnStyle  = { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(34,197,94,0.15)', color: '#166534', border: '1px solid rgba(34,197,94,0.3)' };
const badgeOffStyle = { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-subtle)', color: 'var(--text-muted)', border: '1px solid var(--border)' };
